import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {geographyAssignmentValueValidator, UserAdditionalColumnType as UserColumnType} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplateAdditionalColumns";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

import type {GeographyCollections} from "~/contexts/geographyContext";
import {indexById} from "~/utilities/geographyIndex";

const coordinateAdditionalColumnTypes = new Set<string>([
    UdoColumnType.geographyLatitude,
    UdoColumnType.geographyLongitude,
]);

const referenceAdditionalColumnTypes = new Set<string>([
    UdoColumnType.geographyCountries,
    UdoColumnType.geographyIndiaZones,
    UdoColumnType.geographyIndiaStates,
    UdoColumnType.geographyIndiaCities,
    UdoColumnType.geographyIndiaPincodes,
]);

let referenceLabelsByAdditionalColumnType: ReadonlyMap<string, (id: Uuid) => string | undefined> | null = null;

const userGeographyTypes = new Map<string, UdoColumnType>([
    [UserColumnType.geographyCountries, UdoColumnType.geographyCountries],
    [UserColumnType.geographyIndiaZones, UdoColumnType.geographyIndiaZones],
    [UserColumnType.geographyIndiaStates, UdoColumnType.geographyIndiaStates],
    [UserColumnType.geographyIndiaCities, UdoColumnType.geographyIndiaCities],
    [UserColumnType.geographyIndiaPincodes, UdoColumnType.geographyIndiaPincodes],
    [UserColumnType.geographyLatitude, UdoColumnType.geographyLatitude],
    [UserColumnType.geographyLongitude, UdoColumnType.geographyLongitude],
]);

/** User geography references are arrays; UDO geography references are scalar IDs. */
export function resolveUserGeographyAdditionalColumnValue(type: string, value: unknown): string | null {
    const udoType = userGeographyTypes.get(type);
    if (udoType === undefined) {
        return null;
    }
    if (coordinateAdditionalColumnTypes.has(udoType)) {
        return resolveGeographyAdditionalColumnValue(udoType, value) ?? "-";
    }
    if (value === null || value === undefined) {
        return "-";
    }
    const assignments = geographyAssignmentValueValidator.safeParse(value);
    if (!assignments.success) {
        return "Invalid geography assignment";
    }
    const label = referenceLabelsByAdditionalColumnType?.get(udoType);
    return assignments.data.map((id) => label?.(id) ?? id).join(", ") || "-";
}

export function initializeGeographyValueResolver(collections: GeographyCollections): void {
    const countriesById = indexById(collections.countries);
    const indiaCitiesById = indexById(collections.indiaCities);
    const indiaPincodesById = indexById(collections.indiaPincodes);
    const indiaStatesById = indexById(collections.indiaStates);
    const indiaZonesById = indexById(collections.indiaZones);

    referenceLabelsByAdditionalColumnType = new Map<string, (id: Uuid) => string | undefined>([
        [UdoColumnType.geographyCountries, (id) => countriesById.get(id)?.name],
        [UdoColumnType.geographyIndiaZones, (id) => indiaZonesById.get(id)?.name],
        [UdoColumnType.geographyIndiaStates, (id) => indiaStatesById.get(id)?.name],
        [UdoColumnType.geographyIndiaCities, (id) => indiaCitiesById.get(id)?.name],
        [UdoColumnType.geographyIndiaPincodes, (id) => indiaPincodesById.get(id)?.pincode],
    ]);
}

export function isGeographyAdditionalColumnType(additionalColumnType: unknown): boolean {
    return typeof additionalColumnType === "string"
        && (coordinateAdditionalColumnTypes.has(additionalColumnType) || referenceAdditionalColumnTypes.has(additionalColumnType));
}

export function resolveGeographyAdditionalColumnValue(additionalColumnType: string, value: unknown): string | null {
    if (coordinateAdditionalColumnTypes.has(additionalColumnType)) {
        if (typeof value === "number") {
            return String(value);
        }
        return typeof value === "string" && value.trim() !== "" ? value : "-";
    }

    if (typeof value !== "string") {
        return "-";
    }
    if (referenceLabelsByAdditionalColumnType === null) {
        return null;
    }

    const label = referenceLabelsByAdditionalColumnType.get(additionalColumnType);
    return label === undefined ? null : (label(value as Uuid) ?? "-");
}
